/**
 * 岗位深度分析报告渲染组件
 *
 * 用在 2 处:
 *   - AdminJobNew step 2(创建岗位时展示 LLM 输出)
 *   - AdminJobDetail(岗位详情页展示已存的报告)
 *
 * 8 段结构(部分按需出现):
 *   ① role_essence              本质定位
 *   ② target_persona            候选人画像
 *   ③ capability_model          能力 6 维带权重
 *   ④ hiring_pitfalls           招人坑
 *   ⑤ hidden_signals            隐藏信号
 *   ⑥ red_flag_signals          面试红旗
 *   ⑦ advantage_credibility     优势可信度
 *   ⑧ reverse_challenge         反向 challenge
 */

import type { AnalysisReport } from '../api/types';

export function ReportView({ report }: { report: AnalysisReport }) {
  return (
    <div className="col" style={{ gap: 12, marginBottom: 16 }}>
      {/* 顶部:报告概览 */}
      <div style={{
        padding: '12px 16px',
        background: 'linear-gradient(135deg, var(--accent-soft), var(--info-soft))',
        borderRadius: 10,
        border: '1px solid var(--accent)',
      }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
          🎯 AI 招聘顾问深度诊断报告
        </div>
        <div className="hint" style={{ fontSize: 13 }}>
          基于招聘简章 + 老板原话 + 行业基准三方信息交叉分析,共 {countSections(report)} 段输出
        </div>
      </div>

      {/* ① 本质定位 */}
      {report.role_essence && (
        <Section title="① 岗位本质定位" tone="indigo">
          <div style={{ marginBottom: 10 }}>
            <Label>战略级判断</Label>
            <Para>{toText(report.role_essence.true_positioning)}</Para>
          </div>
          {report.role_essence.common_misframing && (
            <div>
              <Label>常见认知错位</Label>
              <Para muted>{toText(report.role_essence.common_misframing)}</Para>
            </div>
          )}
        </Section>
      )}

      {/* ② 候选人画像 */}
      {report.target_persona && (
        <Section title="② 理想候选人画像" tone="blue">
          {(report.target_persona.ideal_backgrounds ?? []).length > 0 && (
            <Subsection>
              <Label>典型来源(具体到公司+部门)</Label>
              <div className="col" style={{ gap: 8, marginTop: 6 }}>
                {(report.target_persona.ideal_backgrounds ?? []).map((bg, i) => (
                  <div key={i} style={{ padding: 10, background: 'var(--bg-elev)', borderRadius: 6 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{bg.category}</div>
                    <div className="row" style={{ flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                      {(bg.company_examples ?? []).map((c, j) => (
                        <span key={j} className="chip" style={{ fontSize: 12 }}>{c}</span>
                      ))}
                    </div>
                    <div className="hint" style={{ fontSize: 12 }}>{bg.why_fit}</div>
                  </div>
                ))}
              </div>
            </Subsection>
          )}

          <KVRow k="建议工作年限" v={report.target_persona.experience_years} />
          <KVRow k="年龄状态" v={report.target_persona.age_state_hint} />
          <KVRow k="地理因素" v={report.target_persona.geographic_constraint} />
          <KVRow k="薪酬区间" v={report.target_persona.salary_range_yuan_per_year} highlight />

          {(report.target_persona.must_exclude ?? []).length > 0 && (
            <Subsection>
              <Label>应排除的人</Label>
              <div className="col" style={{ gap: 6, marginTop: 6 }}>
                {(report.target_persona.must_exclude ?? []).map((ex, i) => (
                  <div key={i} style={{
                    padding: 8,
                    background: 'var(--danger-soft)',
                    borderLeft: '3px solid var(--danger)',
                    borderRadius: 4,
                    fontSize: 13,
                  }}>
                    <div style={{ fontWeight: 600, color: '#b91c1c', marginBottom: 2 }}>× {ex.type}</div>
                    <div className="hint" style={{ fontSize: 12 }}>{ex.reason}</div>
                  </div>
                ))}
              </div>
            </Subsection>
          )}
        </Section>
      )}

      {/* ③ 能力模型 */}
      {report.capability_model && report.capability_model.length > 0 && (
        <Section title="③ 能力模型(带权重)" tone="emerald">
          <div className="col" style={{ gap: 10 }}>
            {report.capability_model.map((cm, i) => (
              <div key={i} style={{ padding: 12, background: 'var(--bg-elev)', borderRadius: 6 }}>
                <div className="row" style={{ alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{toText(cm.dimension)}</span>
                  <WeightBar pct={Number(cm.weight_pct) || 0} />
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.7, marginBottom: 6, color: 'var(--text-muted)' }}>{toText(cm.description)}</div>
                {cm.how_to_evaluate && (
                  <div style={{
                    fontSize: 12,
                    padding: '6px 10px',
                    background: 'var(--accent-soft)',
                    borderRadius: 4,
                    marginTop: 6,
                    marginBottom: 8,
                  }}>
                    <span style={{ fontWeight: 600, color: 'var(--accent)' }}>面试考察方向:</span> {toText(cm.how_to_evaluate)}
                  </div>
                )}
                {/* 这一维下的具体能力(纲+目结构) */}
                {(cm.sub_capabilities ?? []).length > 0 && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                      ↳ 具体考核能力 ({(cm.sub_capabilities ?? []).length} 条)
                    </div>
                    <div className="col" style={{ gap: 4 }}>
                      {(cm.sub_capabilities ?? []).map((sc, j) => (
                        <div key={j} style={{
                          padding: '6px 10px',
                          background: 'var(--surface)',
                          borderRadius: 4,
                          borderLeft: `3px solid ${sc.priority === 'must_have' ? 'var(--danger)' : 'var(--text-dim)'}`,
                          fontSize: 12,
                        }}>
                          <div className="row" style={{ gap: 6, marginBottom: 2, alignItems: 'baseline' }}>
                            <span style={{
                              fontSize: 10,
                              padding: '1px 6px',
                              borderRadius: 3,
                              background: sc.priority === 'must_have' ? 'var(--danger-soft)' : 'var(--surface-2)',
                              color: sc.priority === 'must_have' ? '#b91c1c' : 'var(--text-muted)',
                              fontWeight: 600,
                            }}>
                              {sc.priority === 'must_have' ? '★必备' : '加分'}
                            </span>
                            <span style={{ fontWeight: 600 }}>{toText(sc.name)}</span>
                          </div>
                          {sc.description && (
                            <div style={{ color: 'var(--text-muted)', fontSize: 11, lineHeight: 1.5 }}>
                              {toText(sc.description)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ④ 招人坑 */}
      {report.hiring_pitfalls && report.hiring_pitfalls.length > 0 && (
        <Section title="④ 招人坑(中小企业老板易踩)" tone="amber">
          <div className="col" style={{ gap: 8 }}>
            {report.hiring_pitfalls.map((p, i) => (
              <div key={i} style={{ padding: 10, background: 'var(--warn-soft)', borderRadius: 6 }}>
                <div style={{ fontWeight: 600, marginBottom: 4, color: '#b45309' }}>⚠️ {toText(p.pitfall)}</div>
                {p.why_dangerous && <div className="hint" style={{ fontSize: 12, marginBottom: 4 }}>为什么危险:{toText(p.why_dangerous)}</div>}
                {p.how_to_avoid && <div style={{ fontSize: 12, color: '#047857' }}>✓ 怎么避开:{toText(p.how_to_avoid)}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ⑤ 隐藏信号 */}
      {report.hidden_signals && report.hidden_signals.length > 0 && (
        <Section title="⑤ 招聘文案的隐藏信号" tone="indigo">
          <div className="col" style={{ gap: 8 }}>
            {report.hidden_signals.map((s, i) => (
              <div key={i} style={{ padding: 10, background: 'var(--info-soft)', borderRadius: 6 }}>
                <div style={{ fontWeight: 600, marginBottom: 4, color: '#4338ca' }}>📍 {toText(s.signal)}</div>
                <div className="hint" style={{ fontSize: 13, marginBottom: 4 }}>{toText(s.interpretation)}</div>
                {s.candidate_implication && (
                  <div style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--text-muted)' }}>
                    候选人应知:{toText(s.candidate_implication)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ⑥ 面试红旗 */}
      {report.red_flag_signals && report.red_flag_signals.length > 0 && (
        <Section title="⑥ 面试红旗信号" tone="amber">
          <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8, fontSize: 13 }}>
            {report.red_flag_signals.map((r, i) => (
              <li key={i}>{toText(r)}</li>
            ))}
          </ul>
        </Section>
      )}

      {/* ⑦ 优势可信度 */}
      {report.advantage_credibility && (
        <Section title="⑦ 招聘方优势 - 可信度评估" tone="blue">
          {(report.advantage_credibility.high_credibility ?? []).length > 0 && (
            <Subsection>
              <Label color="#047857">✓ 真金白银(可信)</Label>
              <ul style={{ margin: 6, paddingLeft: 20, fontSize: 13 }}>
                {(report.advantage_credibility.high_credibility ?? []).map((x, i) => (
                  <li key={i}><strong>{toText(x.advantage)}</strong> — {toText(x.evidence)}</li>
                ))}
              </ul>
            </Subsection>
          )}
          {(report.advantage_credibility.needs_verification ?? []).length > 0 && (
            <Subsection>
              <Label color="#b45309">? 需要尽调</Label>
              <ul style={{ margin: 6, paddingLeft: 20, fontSize: 13 }}>
                {(report.advantage_credibility.needs_verification ?? []).map((x, i) => (
                  <li key={i}>
                    <strong>{toText(x.advantage)}</strong>
                    {x.what_to_dig && <div className="hint" style={{ fontSize: 12 }}>尽调:{toText(x.what_to_dig)}</div>}
                  </li>
                ))}
              </ul>
            </Subsection>
          )}
        </Section>
      )}

      {/* ⑧ 反向 challenge */}
      {report.reverse_challenge && (
        <Section title="⑧ 给招聘方的反向 challenge" tone="amber">
          <div style={{
            padding: 12,
            background: 'var(--warn-soft)',
            borderLeft: '3px solid var(--warn)',
            borderRadius: 4,
            fontSize: 14,
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
          }}>
            {toText(report.reverse_challenge)}
          </div>
        </Section>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────
// 小组件
// ────────────────────────────────────────────────

function Section({
  title, tone, children,
}: { title: string; tone: 'blue' | 'emerald' | 'amber' | 'indigo'; children: React.ReactNode }) {
  return (
    <div className="card" style={{ marginBottom: 0, padding: 16 }}>
      <h3 style={{ marginBottom: 10, fontSize: 15, color: 'var(--text)', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function Subsection({ children }: { children: React.ReactNode }) {
  return <div style={{ marginBottom: 10 }}>{children}</div>;
}

function Label({ children, color }: { children: React.ReactNode; color?: string }) {
  return <div style={{ fontSize: 12, fontWeight: 600, color: color || 'var(--text-muted)', marginBottom: 2 }}>{children}</div>;
}

function Para({ children, muted }: { children: React.ReactNode; muted?: boolean }) {
  return <div style={{ fontSize: 13, lineHeight: 1.7, color: muted ? 'var(--text-muted)' : 'var(--text)' }}>{children}</div>;
}

function KVRow({ k, v, highlight }: { k: string; v?: unknown; highlight?: boolean }) {
  const text = toText(v);
  if (!text) return null;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', fontSize: 13, alignItems: 'baseline' }}>
      <div style={{ minWidth: 80, color: 'var(--text-muted)', flexShrink: 0 }}>{k}</div>
      <div style={{
        flex: 1,
        fontWeight: highlight ? 600 : 400,
        color: highlight ? 'var(--accent)' : 'var(--text)',
      }}>{text}</div>
    </div>
  );
}

function WeightBar({ pct }: { pct: number }) {
  return (
    <span className="row" style={{ gap: 6, alignItems: 'center', fontSize: 12 }}>
      <span style={{ position: 'relative', width: 80, height: 6, background: 'var(--surface-2)', borderRadius: 3, overflow: 'hidden' }}>
        <span style={{
          position: 'absolute', inset: 0,
          width: `${Math.min(pct, 100)}%`,
          background: 'var(--accent)',
        }} />
      </span>
      <span style={{ color: 'var(--accent)', fontWeight: 600, minWidth: 30 }}>{pct}%</span>
    </span>
  );
}


/**
 * LLM 偶尔会把字符串字段输出成 ["1. xxx", "2. yyy"] 数组,React 不能直接渲染数组。
 * 此 helper 接受 string / string[] / null / undefined,统一返回字符串。
 */
function toText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map((x) => toText(x)).filter(Boolean).join('\n');
  if (typeof v === 'object') {
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
  }
  return String(v);
}


function countSections(report: AnalysisReport): number {
  let n = 0;
  if (report.role_essence) n++;
  if (report.target_persona) n++;
  if (report.capability_model?.length) n++;
  if (report.hiring_pitfalls?.length) n++;
  if (report.hidden_signals?.length) n++;
  if (report.red_flag_signals?.length) n++;
  if (report.advantage_credibility) n++;
  if (report.reverse_challenge) n++;
  return n;
}
